"use client";

import { useCallback, useEffect, useState } from "react";
import { useLanguage } from "@/utils/LanguageContext";

const STORAGE_KEY = "invtron-exit-intent-dismissed";
const TELEGRAM_URL = "https://t.me/Invtrondaogroup";

const ExitIntentPrompt = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [ready, setReady] = useState(false);
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    setDismissed(sessionStorage.getItem(STORAGE_KEY) === "1");
    const timer = window.setTimeout(() => setReady(true), 8000);
    return () => window.clearTimeout(timer);
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    setDismissed(true);
    sessionStorage.setItem(STORAGE_KEY, "1");
  }, []);

  useEffect(() => {
    if (!ready || dismissed) return;

    const handleMouseOut = (event: MouseEvent) => {
      if (event.clientY > 0) return;
      if (event.relatedTarget) return;
      setVisible(true);
    };

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        setVisible(true);
      }
    };

    document.addEventListener("mouseout", handleMouseOut);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      document.removeEventListener("mouseout", handleMouseOut);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [dismissed, ready]);

  if (!visible) return null;

  return (
    <div className="exit-intent-backdrop" role="presentation">
      <div
        className="exit-intent-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="exit-intent-title"
      >
        <button
          type="button"
          className="exit-intent-close"
          aria-label="Close"
          onClick={dismiss}
        >
          ×
        </button>
        <h3 id="exit-intent-title" className="exit-intent-title">
          {t("Before You Go!")}
        </h3>
        <p className="exit-intent-copy">
          {t("Stay connected with Invtron and get involved with our Ecosystem.")}
        </p>
        <div className="exit-intent-actions">
          <a
            href={TELEGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="ts-buy-btn"
            onClick={dismiss}
          >
            {t("Click Here to Contribute!")}
          </a>
        </div>
      </div>
      <style jsx>{`
        .exit-intent-backdrop {
          position: fixed;
          inset: 0;
          z-index: 2200;
          background: rgba(2, 10, 20, 0.72);
          display: grid;
          place-items: center;
          padding: 18px;
        }

        .exit-intent-modal {
          width: min(560px, 96vw);
          position: relative;
          padding: clamp(20px, 4vw, 30px);
          border-radius: 24px;
          border: 1px solid rgba(255, 255, 255, 0.15);
          background:
            radial-gradient(circle at top left, rgba(88, 170, 255, 0.22), transparent 40%),
            linear-gradient(165deg, rgba(10, 36, 64, 0.96), rgba(5, 18, 34, 0.95));
          box-shadow: 0 30px 80px rgba(0, 0, 0, 0.38);
        }

        .exit-intent-close {
          position: absolute;
          top: 10px;
          right: 10px;
          width: 38px;
          height: 38px;
          border-radius: 50%;
          border: 1px solid rgba(255, 255, 255, 0.22);
          background: rgba(255, 255, 255, 0.08);
          color: #fff;
          font-size: 1.6rem;
          line-height: 1;
        }

        .exit-intent-title {
          margin: 0 0 10px;
          color: #e5f3fd;
        }

        .exit-intent-copy {
          margin: 0;
          color: rgba(225, 238, 255, 0.84);
          line-height: 1.7;
        }

        .exit-intent-actions {
          margin-top: 20px;
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        .exit-intent-actions .ts-buy-btn {
          flex: 1 1 220px;
          justify-content: center;
          text-align: center;
        }
      `}</style>
    </div>
  );
};

export default ExitIntentPrompt;
